import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import { GradientBackground, ModalAura, type CursorZone } from "@/components/backgrounds/GradientBackground";
import { DesktopSignin } from "@/components/login/DesktopSignin";
import { LoginFooter } from "@/components/login/LoginFooter";
import { Text } from "@/components/podkit/typography/Text";
import { useLocalRunner } from "@/hooks/use-local-runner";
import { useListLoginProviders } from "@/queries/account-queries";
import { SSOLogin } from "@/routes/login/LoginPage";
import FreeCreditsPromotionBanner from "@/routes/ona-onboarding/FreeCreditsPromotionBanner";
import { useState, useCallback, useRef, type MouseEvent } from "react";

type OnaOnboardingLayoutProps = {
    returnTo: string;
    inviteId?: string;
};

/**
 * Determines which zone of a 3x3 grid the cursor is in.
 */
function getCursorZone(x: number, y: number): CursorZone {
    if (y < 0.33) {
        if (x < 0.33) return "top-left";
        if (x > 0.66) return "top-right";
        return "top";
    }
    if (y > 0.66) {
        if (x < 0.33) return "bottom-left";
        if (x > 0.66) return "bottom-right";
        return "bottom";
    }
    if (x < 0.33) return "left";
    if (x > 0.66) return "right";
    return "center";
}

export function OnaOnboardingLayout({ returnTo, inviteId }: OnaOnboardingLayoutProps) {
    const [showContinueWithSSO, setShowContinueWithSSO] = useState(false);
    const [cursorZone, setCursorZone] = useState<CursorZone>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const lastZoneRef = useRef<CursorZone>(null);

    const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
        if (!modalRef.current) return;

        const rect = modalRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        const zone = getCursorZone(x, y);

        // Only update state if zone changed to avoid unnecessary re-renders
        if (zone !== lastZoneRef.current) {
            lastZoneRef.current = zone;
            setCursorZone(zone);
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        lastZoneRef.current = null;
        setCursorZone(null);
    }, []);

    return (
        <div
            data-testid="ona-onboarding-login-page"
            className="relative flex h-full min-h-screen w-full flex-col items-center justify-between p-0 text-gray-900 md:p-6"
        >
            <GradientBackground className="absolute inset-0" />
            <div className="relative z-10 flex w-128 max-w-full flex-1 flex-col items-center justify-center px-4 md:px-0">
                <div className="relative mx-8 mb-8 mt-4 w-full">
                    {/* Aura effect around the modal */}
                    <ModalAura cursorZone={cursorZone} />
                    
                    {/* Modal card */}
                    <div
                        ref={modalRef}
                        className="relative flex w-full flex-col items-center overflow-hidden rounded-2xl border border-border-base bg-white/80 shadow-lg backdrop-blur-md dark:bg-surface-glass/80 dark:text-white"
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                    >
                        {/* Promotion banner at top of modal */}
                        {!showContinueWithSSO && (
                            <FreeCreditsPromotionBanner className="w-full rounded-none border-0 border-b border-b-border-subtle px-5 pt-3 shadow-none" />
                        )}
                        
                        {/* Ona Branding */}
                        <OnaWordmark data-testid="ona-wordmark" className="m-2 mb-4 mt-10 h-10 w-auto" />
                        <Text className="mb-8 text-center text-inherit opacity-60 px-6">
                            The AI Software Engineer you can rely on.
                        </Text>

                        <div className="flex w-full flex-col items-center space-y-2 px-6 pb-8">
                            <LoginArea inviteId={inviteId} returnTo={returnTo} onSSOViewChange={setShowContinueWithSSO} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer at bottom of page */}
            <div className="relative z-10 mb-5 w-96 max-w-full text-center">
                <LoginFooter className="text-sm text-content-primary" linkClassName="text-sm text-content-primary underline" />
            </div>
        </div>
    );
}

function LoginArea({
    inviteId,
    returnTo,
    onSSOViewChange,
}: {
    inviteId?: string;
    returnTo: string;
    onSSOViewChange: (show: boolean) => void;
}) {
    const { isPending: isPendingProviders } = useListLoginProviders(inviteId);
    const localRunner = useLocalRunner();

    if (inviteId && isPendingProviders) {
        return (
            <p className="mb-6 flex items-center space-x-2 opacity-80">
                <IconSpinner size="lg" className="animate-spin" />
                <span>Loading login options…</span>
            </p>
        );
    }

    return (
        <>
            {localRunner.status && <DesktopSignin />}
            {!localRunner.status && (
                <SSOLogin
                    inviteId={inviteId}
                    returnTo={returnTo}
                    className="w-full"
                    hideLoginToClassic
                    newLoginLayout
                    onSSOViewChange={onSSOViewChange}
                />
            )}
        </>
    );
}
